import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Plus, Search, Star, Upload } from "lucide-react";
import { formatMoney, taxFromNet } from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  Input,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { keepPreviousData } from "@tanstack/react-query";
import {
  SupplierChips,
  SupplierTags,
  shownPrices,
  useDebounced,
  useSupplierFilter,
} from "@/components/positions/SupplierFilter";
import { ArticleDialog } from "./ArticleDialog";
import { ImportDialog } from "./ImportDialog";
import { CatalogUpdateDialog } from "./CatalogUpdateDialog";
import { SuppliersDialog } from "./SuppliersDialog";

export function Articles() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const addToast = useAppStore((s) => s.addToast);
  const consume = trpc.connector.consumeCart.useMutation({
    onSuccess: (r) => {
      if (r.lines.length) {
        sessionStorage.setItem(
          "mavatech_punchout_cart",
          JSON.stringify(r.lines),
        );
        addToast({
          type: "success",
          title: `Warenkorb: ${r.lines.length} Position(en)`,
          description: "Wird in ein neues Angebot übernommen.",
        });
        navigate("/angebote/neu");
      } else {
        addToast({
          type: "info",
          title: "Warenkorb leer oder bereits übernommen",
        });
      }
    },
  });
  useEffect(() => {
    const ref = params.get("punchout");
    if (ref) {
      setParams({}, { replace: true });
      consume.mutate({ sessionRef: ref });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [search, setSearch] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    articleId?: string | null;
  }>({ open: false });

  // Deep-Link von der globalen Schnellsuche (Cmd/Strg+K) — /artikel?open=<id>
  // (AUDIT-2026-10-01-UX.md, Punkt 3.1).
  useEffect(() => {
    const openId = params.get("open");
    if (openId) {
      setParams({}, { replace: true });
      setDialog({ open: true, articleId: openId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [importOpen, setImportOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [suppliersOpen, setSuppliersOpen] = useState(false);

  const [supplierId, setSupplierId] = useSupplierFilter();
  const dSearch = useDebounced(search, 250);
  const list = trpc.article.list.useQuery(
    {
      search: dSearch || undefined,
      favoritesOnly: favOnly || undefined,
      supplierId: supplierId || undefined,
    },
    { placeholderData: keepPreviousData },
  );
  const { sorted, sort, toggle } = useSort(list.data ?? [], {
    name: (a) => a.name,
    number: (a) => a.ownNumber,
    unit: (a) => a.unit,
    listNet: (a) =>
      a.prices
        .map((p) => p.listPriceCents)
        .filter((x): x is number => x != null)
        .sort((x, y) => x - y)[0],
    purchase: (a) => a.bestPurchasePriceCents,
    suppliers: (a) => a.prices.length,
  });
  const org = trpc.organization.get.useQuery();
  const taxRate =
    (org.data as { defaultTaxRate?: number } | undefined)?.defaultTaxRate ?? 20;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Artikel"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setSuppliersOpen(true)}>
              <Building2 className="w-4 h-4" /> Lieferanten
            </Button>
            <Button variant="ghost" onClick={() => setImportOpen(true)}>
              <Upload className="w-4 h-4" /> CSV-Import
            </Button>
            <Button variant="secondary" onClick={() => setCatalogOpen(true)}>
              <Upload className="w-4 h-4" /> Katalog aktualisieren
            </Button>
            <Button variant="primary" onClick={() => setDialog({ open: true })}>
              <Plus className="w-4 h-4" /> Neuer Artikel
            </Button>
          </div>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Name, Nummer, EAN, Warengruppe …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={favOnly}
            onChange={(e) => setFavOnly(e.target.checked)}
          />
          nur Favoriten
        </label>
        <SupplierChips value={supplierId} onChange={setSupplierId} />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !list.data?.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {search
              ? "Keine Treffer."
              : "Noch keine Artikel. Lege welche an oder importiere einen Katalog."}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortTh
                  label="Bezeichnung"
                  sortKey="name"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Nummer"
                  sortKey="number"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Einheit"
                  sortKey="unit"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Listenpreis netto"
                  sortKey="listNet"
                  sort={sort}
                  onSort={toggle}
                  className="text-right"
                />
                <SortTh
                  label="Listenpreis brutto"
                  sortKey="listNet"
                  sort={sort}
                  onSort={toggle}
                  className="text-right"
                />
                <SortTh
                  label="Einkaufspreis netto"
                  sortKey="purchase"
                  sort={sort}
                  onSort={toggle}
                  className="text-right"
                />
                <SortTh
                  label="Händler"
                  sortKey="suppliers"
                  sort={sort}
                  onSort={toggle}
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const shown = shownPrices(
                  a.prices,
                  supplierId || undefined,
                  a.bestPurchasePriceCents,
                );
                const listNet = shown.list;
                const listGross =
                  listNet != null
                    ? listNet + taxFromNet(listNet, taxRate)
                    : null;
                return (
                  <tr
                    key={a.id}
                    className="cursor-pointer"
                    onClick={() => setDialog({ open: true, articleId: a.id })}
                  >
                    <td className="text-text-primary">
                      <span className="flex items-center gap-1.5">
                        {a.isFavorite && (
                          <Star className="w-3.5 h-3.5 text-accent fill-accent" />
                        )}
                        {a.name}
                      </span>
                      {/* Warengruppe ist aktuell nur der rohe Datanorm-Code (z. B. "IIO") ohne
                          Klartext-Zuordnung (datanorm.wrg noch nicht importiert, siehe BACKLOG.md)
                          — für Nutzer ohne Erklärung bedeutungslos, daher hier ausgeblendet statt
                          einen kryptischen Code zu zeigen (AUDIT-2026-10-01-UX.md, Punkt 2.4). In
                          der Artikel-Detailansicht bleibt er sichtbar, dort mit Feldbezeichnung
                          "Warengruppe". */}
                    </td>
                    <td className="text-text-secondary font-mono text-xs">
                      {a.ownNumber ?? "—"}
                    </td>
                    <td className="text-text-secondary">{a.unit}</td>
                    <td className="text-right text-money text-text-secondary">
                      {listNet != null ? formatMoney(listNet) : "—"}
                    </td>
                    <td className="text-right text-money text-text-tertiary">
                      {listGross != null ? formatMoney(listGross) : "—"}
                    </td>
                    <td className="text-right text-money text-text-secondary">
                      {shown.purchase != null
                        ? formatMoney(shown.purchase)
                        : "—"}
                    </td>
                    <td>
                      {a.prices.length ? (
                        <SupplierTags
                          prices={a.prices}
                          supplierId={supplierId}
                        />
                      ) : (
                        <span className="text-text-tertiary text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ArticleDialog
        state={dialog}
        onClose={() => setDialog({ open: false })}
      />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <CatalogUpdateDialog open={catalogOpen} onOpenChange={setCatalogOpen} />
      <SuppliersDialog open={suppliersOpen} onOpenChange={setSuppliersOpen} />
    </div>
  );
}
